import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

// Reads the QR code printed on a bill (GST e-invoice / e-way bill / UPI) from a
// photo. The backend trusts these values over what OCR/AI read off the photo.

const READER_ID = 'ebill-qr-reader';
let reader = null;

const getReader = () => {
  if (!document.getElementById(READER_ID)) {
    const el = document.createElement('div');
    el.id = READER_ID;
    el.style.display = 'none';
    document.body.appendChild(el);
    reader = null;
  }
  if (!reader) {
    reader = new Html5Qrcode(READER_ID, {
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      verbose: false
    });
  }
  return reader;
};

const loadImage = (blob) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
  img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
  img.src = url;
});

const canvasToFile = (canvas, name) => new Promise(resolve => {
  canvas.toBlob(b => resolve(b ? new File([b], name, { type: 'image/png' }) : null), 'image/png');
});

// Extra attempts for webcam photos: the QR region enlarged, and a high-contrast copy
const makeVariants = async (blob) => {
  const img = await loadImage(blob);
  const variants = [];
  const crops = [
    { x: 0, y: 0, w: 1, h: 1 },          // whole photo, high contrast
    { x: 0.5, y: 0, w: 0.5, h: 0.6 },    // top-right (e-invoice QR usually sits here)
    { x: 0, y: 0, w: 0.5, h: 0.6 },      // top-left
    { x: 0.2, y: 0.2, w: 0.6, h: 0.6 }   // centre
  ];
  for (const c of crops) {
    const sw = img.width * c.w;
    const sh = img.height * c.h;
    const scale = Math.min(2, 2000 / Math.max(sw, sh));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(sw * scale);
    canvas.height = Math.round(sh * scale);
    const ctx = canvas.getContext('2d');
    ctx.filter = 'grayscale(1) contrast(1.6)';
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, img.width * c.x, img.height * c.y, sw, sh, 0, 0, canvas.width, canvas.height);
    const file = await canvasToFile(canvas, 'qr-variant.png');
    if (file) variants.push(file);
  }
  return variants;
};

const tryDecode = async (file) => {
  try {
    const result = await getReader().scanFileV2(file, false);
    return result?.decodedText || '';
  } catch (e) {
    return '';
  }
};

/**
 * Returns the bill's QR text, or '' when no QR could be read.
 * `thorough` also tries enlarged / high-contrast crops (slower; use on a captured photo).
 */
export const decodeQrFromImage = async (blob, { thorough = true } = {}) => {
  if (!blob || !String(blob.type || 'image/').startsWith('image/')) return '';
  const file = blob instanceof File ? blob : new File([blob], 'ebill.jpg', { type: blob.type || 'image/jpeg' });
  const direct = await tryDecode(file);
  if (direct || !thorough) return direct;
  try {
    for (const variant of await makeVariants(blob)) {
      const text = await tryDecode(variant);
      if (text) return text;
    }
  } catch (e) {
    console.warn('QR variant decode failed', e);
  }
  return '';
};
